/**
 * WeekStrip component — shared types and Zod prop schema.
 *
 * A horizontal, data-driven week calendar ("Calendar" in Figma): a row of DateChip
 * chips. Stateless — selection comes from the `days` data or a bound
 * `selectedDate`, and the host owns it. Pressing a day writes it to the bound
 * selection and emits `select`; when the pressed day was already the selected one,
 * it also emits `selected_press`. The events carry no payload — the day reaches a
 * document through the bound state path.
 * Compound component (ADR-020
 * Rule 5): composes standalone DateChip children and owns only the inter-child
 * layout.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { actionIdSchema } from '@galapagos/product-analytics';
import { spacing } from '../../elements/spacing';
import { dateChipPropsSchema, type DateChipState } from '../DateChip/DateChip';

/** A day item: the DateChip chip's data props plus its `date` identity. */
export const weekStripDaySchema = dateChipPropsSchema.extend({
  date: z.string(),
  /** Optional stable automation identity for this specific day chip. */
  testID: z.string().optional(),
  /**
   * Whether this day can be selected. Defaults to selectable; set `false` to
   * ignore presses (e.g. future days in a calendar). A non-selectable day does
   * not fire `onSelectDate`, is announced as disabled, and its chip draws the
   * disabled look; a selected or today chip keeps its pill.
   */
  selectable: z.boolean().optional(),
});

export type WeekStripDay = z.infer<typeof weekStripDaySchema>;

/**
 * Input shape for a day item — `state` is optional here (the schema/DateChip chip
 * default it to 'default'). Platform components accept this looser shape so they
 * compose with the catalog's pre-default (z.input) props.
 */
export type WeekStripDayInput = z.input<typeof weekStripDaySchema>;

/**
 * WeekStrip prop schema for the catalog (ADR-011). Data only — the
 * `onSelectDate` callback, container `testID`, and `accessibilityLabel` are runtime props
 * on the platform-specific interfaces.
 */
export const weekStripPropsSchema = z.object({
  actionId: actionIdSchema.optional(),
  days: z.array(weekStripDaySchema),
  /**
   * Optional selected-day identity. When set, it is the single source of truth
   * for selection — a day renders selected iff its `date` matches — overriding
   * any per-day `state`. This is the bindable scalar a GenUI/json-render host
   * drives via `useBoundProp`; omit it to let each day's own `state` decide.
   */
  selectedDate: z.string().optional(),
  /** Read-only status strip; removes selection/radio semantics from the row and chips. */
  presentation: z.boolean().optional(),
});

export type WeekStripProps = z.infer<typeof weekStripPropsSchema>;

/**
 * Resolve a day's effective selection state. A bound `selectedDate` wins over
 * the per-day `state`, so the catalog/registry path (which binds a single
 * scalar) and the direct-prop path (which sets per-day `state`) share one rule.
 */
export function resolveWeekStripDayState(
  date: string,
  dayState: DateChipState | undefined,
  selectedDate: string | undefined,
): DateChipState | undefined {
  if (selectedDate !== undefined) {
    return date === selectedDate ? 'selected' : 'default';
  }
  return dayState;
}

/**
 * Whether `date` is the day the strip renders selected, using the same rule the
 * chip renders with — so a press is judged against the highlight the member saw.
 *
 * Callers pass the `selectedDate` the strip was rendered with, and MUST call this
 * before writing the pressed day back to the bound selection: judged after that
 * write, every pressed day reads as already selected. A date absent from `days`
 * is not selected.
 */
export function isWeekStripDaySelected(
  date: string,
  days: readonly WeekStripDayInput[],
  selectedDate: string | undefined,
): boolean {
  const day = days.find((candidate) => candidate.date === date);
  if (!day) return false;
  return resolveWeekStripDayState(day.date, day.state, selectedDate) === 'selected';
}

/** Stable automation id for a strip day chip (today alias per ADR-035). */
export function resolveWeekStripDayTestID(
  day: WeekStripDayInput,
  containerTestID: string | undefined,
): string | undefined {
  if (day.testID) return day.testID;
  if (!containerTestID) return undefined;
  if (day.today) return `${containerTestID}-today`;
  return `${containerTestID}-${day.date}`;
}

/** Layout — inter-chip gap; the strip distributes days across the row width. */
export const WEEK_STRIP_GAP = spacing.xs;
export const WEEK_STRIP_PADDING_VERTICAL = spacing.xs;
